/**
 * 🧱 Refactor BT Phase 3 — section « SectionEchafaudsCreer » de CreerBonTravailDialog.jsx : JSX déplacé TEL QUEL (ex-L1205-1455, dédenté de 10) ;
 * variables via `useBTForm()` (BTFormProvider enrichi par `extra`). DOM, data-testid, classes, textes inchangés.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageIcon, RefreshCw, Wrench } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useBTForm } from '../../BTFormContext';

export function SectionEchafaudsCreer() {
  const {
    calculEnCours,
    calculerPiecesEchafauds,
    form,
    isExt,
    remorquesEchafauds,
    setForm,
    type,
  } = useBTForm();
  return (
    <>
      {/* Équipements de location - Échafaudages (extérieur seulement) */}
      {isExt && remorquesEchafauds.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4" /> Équipements de location - Échafaudages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Mode d'activation */}
            {!form.lavage_pression.echafauds_a_monter && (
              <label className="flex items-center gap-2 cursor-pointer bg-orange-50 p-3 rounded-lg border border-orange-200">
                <input 
                  type="checkbox" 
                  checked={form.echafaudages.actif && form.echafaudages.source === 'manuel'} 
                  onChange={e => setForm(f => ({...f, echafaudages: {...f.echafaudages, actif: e.target.checked, source: e.target.checked ? 'manuel' : ''}}))}
                  className="w-4 h-4 rounded border-orange-300 text-orange-600" 
                />
                <span className="text-sm font-medium text-orange-700">Activer les échafaudages pour ce BT</span>
              </label>
            )}

            {/* Information si vient du lavage */}
            {form.lavage_pression.echafauds_a_monter && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800">Échafaudages activés pour le lavage à pression</p>
                  <p className="text-xs text-blue-600 mt-1">
                    📅 Montage : {form.dates_chantier[0] || 'À définir'} (début des travaux) • Démontage : À choisir ci-dessous
                  </p>
                </div>
              </div>
            )}

            {/* Configuration échafaudages (si actif) */}
            {(form.echafaudages.actif || form.lavage_pression.echafauds_a_monter) && (
              <div className="space-y-4 border-t border-orange-200 pt-4">
                {/* Sélection remorque et type */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Remorque d'échafauds</Label>
                    <Select 
                      value={form.echafaudages.remorque_id} 
                      onValueChange={v => setForm(f => ({...f, echafaudages: {...f.echafaudages, remorque_id: v, type_etage_id: '', pieces_calculees: [], pieces_manuelles: []}}))}>
                      <SelectTrigger className="mt-1 h-9 text-sm">
                        <SelectValue placeholder="Sélectionnez une remorque" />
                      </SelectTrigger>
                      <SelectContent>
                        {remorquesEchafauds.map(r => (
                          <SelectItem key={r.id} value={r.id}>{r.numero}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {form.echafaudages.remorque_id && (() => {
                    const remorque = remorquesEchafauds.find(r => r.id === form.echafaudages.remorque_id);
                    const types = remorque?.types_etages || [];
                    return (
                      <div>
                        <Label className="text-xs">Type d'étage</Label>
                        <Select 
                          value={form.echafaudages.type_etage_id} 
                          onValueChange={v => setForm(f => ({...f, echafaudages: {...f.echafaudages, type_etage_id: v, pieces_calculees: [], pieces_manuelles: []}}))}>
                          <SelectTrigger className="mt-1 h-9 text-sm">
                            <SelectValue placeholder="Choisir un type" />
                          </SelectTrigger>
                          <SelectContent>
                            {types.map(t => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.type} ({t.dimensions}) — {t.quantite_disponible} dispo
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
                </div>

                {/* Dates de montage/démontage */}
                <div className="grid grid-cols-2 gap-3">
                  {form.echafaudages.source === 'manuel' && (
                    <div>
                      <Label className="text-xs">Date de montage</Label>
                      <Input 
                        type="date" 
                        value={form.echafaudages.date_montage} 
                        onChange={e => setForm(f => ({...f, echafaudages: {...f.echafaudages, date_montage: e.target.value}}))}
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                  )}
                  <div className={form.echafaudages.source === 'manuel' ? '' : 'col-span-2'}>
                    <Label className="text-xs">Date de démontage {form.lavage_pression.echafauds_a_monter && <span className="text-orange-600">*</span>}</Label>
                    <Input 
                      type="date" 
                      value={form.echafaudages.date_demontage} 
                      onChange={e => setForm(f => ({...f, echafaudages: {...f.echafaudages, date_demontage: e.target.value}}))}
                      className="mt-1 h-9 text-sm"
                    />
                  </div>
                </div>

                {/* Association à une zone */}
                {form.zones.length > 0 && (
                  <div>
                    <Label className="text-xs">Associer à une zone (optionnel)</Label>
                    <Select 
                      value={form.echafaudages.zone_id || '__none__'} 
                      onValueChange={v => setForm(f => ({...f, echafaudages: {...f.echafaudages, zone_id: v === '__none__' ? null : v}}))}>
                      <SelectTrigger className="mt-1 h-9 text-sm">
                        <SelectValue placeholder="Aucune zone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Aucune zone</SelectItem>
                        {form.zones.map((z, idx) => (
                          <SelectItem key={idx} value={idx.toString()}>
                            Zone {z.numero} {z.nom ? `- ${z.nom}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.echafaudages.zone_id !== null && form.echafaudages.zone_id !== '__none__' && (() => {
                      const zone = form.zones[parseInt(form.echafaudages.zone_id)];
                      return zone?.photo_url ? (
                        <button 
                          type="button"
                          onClick={() => window.open(zone.photo_url, '_blank')}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 underline">
                          <ImageIcon className="w-3 h-3" />
                          Voir la photo de la zone {zone.numero}
                        </button>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Quantité d'étages et calcul */}
                {form.echafaudages.type_etage_id && (
                  <div className="space-y-3">
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Nombre d'étages nécessaires</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          value={form.echafaudages.quantite_etages || ''} 
                          onChange={e => setForm(f => ({...f, echafaudages: {...f.echafaudages, quantite_etages: parseInt(e.target.value) || 0}}))}
                          placeholder="8"
                          className="mt-1 h-9"
                        />
                      </div>
                      <Button 
                        type="button"
                        size="sm" 
                        onClick={calculerPiecesEchafauds}
                        disabled={calculEnCours || !form.echafaudages.quantite_etages}
                        className="bg-orange-600 hover:bg-orange-700 text-white h-9">
                        {calculEnCours ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Calculer les pièces
                      </Button>
                    </div>

                    {/* Résultat du calcul */}
                    {form.echafaudages.pieces_calculees.length > 0 && (
                      <div className="border border-orange-300 rounded-lg p-3 bg-orange-50/50 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-orange-800">Pièces nécessaires (calculées)</p>
                          <span className="text-xs text-orange-600">Vous pouvez ajuster manuellement</span>
                        </div>
                        <div className="space-y-2">
                          {form.echafaudages.pieces_calculees.map((piece, idx) => {
                            const manuelle = form.echafaudages.pieces_manuelles.find(p => p.nom === piece.nom);
                            return (
                              <div key={piece.nom} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded border border-orange-200">
                                <div className="col-span-4">
                                  <p className="text-sm font-medium text-slate-700">{piece.nom}</p>
                                  <p className="text-xs text-slate-500">
                                    {piece.disponible ? (
                                      <span className="text-emerald-600">✓ Disponible</span>
                                    ) : (
                                      <span className="text-red-600">✗ Stock insuffisant ({piece.quantite_disponible} dispo)</span>
                                    )}
                                  </p>
                                </div>
                                <div className="col-span-3">
                                  <p className="text-xs text-slate-500">Calculé</p>
                                  <p className="text-sm font-semibold text-slate-900">{piece.quantite_necessaire}</p>
                                </div>
                                <div className="col-span-3">
                                  <Label className="text-xs text-slate-500">Quantité finale</Label>
                                  <Input 
                                    type="number" 
                                    min="0"
                                    value={manuelle?.quantite || piece.quantite_necessaire} 
                                    onChange={e => {
                                      const newQte = parseInt(e.target.value) || 0;
                                      setForm(f => ({
                                        ...f,
                                        echafaudages: {
                                          ...f.echafaudages,
                                          pieces_manuelles: f.echafaudages.pieces_manuelles.map(p => 
                                            p.nom === piece.nom ? {...p, quantite: newQte} : p
                                          )
                                        }
                                      }));
                                    }}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="col-span-2 text-right">
                                  <p className="text-xs text-slate-500">Prix/jour</p>
                                  <p className="text-sm font-semibold text-slate-700">{(piece.prix_jour * (manuelle?.quantite || piece.quantite_necessaire)).toFixed(2)} $</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="pt-2 border-t border-orange-300 flex justify-between items-center">
                          <p className="text-xs text-slate-600">Total location/jour:</p>
                          <p className="text-lg font-bold text-orange-700">
                            {form.echafaudages.pieces_calculees.reduce((sum, p) => {
                              const manuelle = form.echafaudages.pieces_manuelles.find(pm => pm.nom === p.nom);
                              return sum + (p.prix_jour * (manuelle?.quantite || p.quantite_necessaire));
                            }, 0).toFixed(2)} $
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes sur le montage */}
                <div>
                  <Label className="text-xs">Notes sur le montage des échafauds</Label>
                  <Textarea 
                    value={form.echafaudages.notes} 
                    onChange={e => setForm(f => ({...f, echafaudages: {...f.echafaudages, notes: e.target.value}}))}
                    rows={2}
                    placeholder="Instructions spéciales, précautions, accès, etc..."
                    className="mt-1 text-sm"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}


    </>
  );
}
